
import React, { useState, useCallback } from 'react';
import { Company, User } from '../types';
import { api } from '../services/apiService';

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

interface Session {
  login: string;
  logout: string;
  duration: number;
  type: string;
}

interface UserStat {
  user_id: string;
  user_name: string;
  total_seconds: number;
  session_count: number;
  sessions: Session[];
}

interface ConnectionTimeTabProps {
  company: Company;
  users: User[];
  onCompanyUpdate: (c: Company) => void;
  onUserUpdate?: (u: User) => void;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

function formatDateTime(dt: string): string {
  return new Date(dt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function barWidth(val: number, max: number): string {
  if (max === 0) return '0%';
  return `${Math.round((val / max) * 100)}%`;
}

const PRESET_MINUTES = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const ConnectionTimeTab: React.FC<ConnectionTimeTabProps> = ({ company, users, onCompanyUpdate, onUserUpdate }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [stats, setStats] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Timeout compagnie
  const [companyTimeout, setCompanyTimeout] = useState<number>(company.inactivityTimeout ?? 10);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyMsg, setCompanyMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Timeout par utilisateur
  const [userTimeouts, setUserTimeouts] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    users.forEach(u => { init[u.id] = u.inactivityTimeout !== undefined ? String(u.inactivityTimeout) : ''; });
    return init;
  });
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [userMsg, setUserMsg] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});

  const handleSaveCompanyTimeout = async () => {
    setSavingCompany(true);
    setCompanyMsg(null);
    try {
      const updated = await api.updateCompany(company.id, { inactivityTimeout: companyTimeout });
      onCompanyUpdate(updated);
      setCompanyMsg({ type: 'success', text: 'Délai mis à jour' });
    } catch {
      setCompanyMsg({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSavingCompany(false);
      setTimeout(() => setCompanyMsg(null), 3000);
    }
  };

  const handleSaveUserTimeout = async (userId: string) => {
    setSavingUserId(userId);
    const raw = userTimeouts[userId];
    const value = raw === '' ? null : parseInt(raw, 10);
    try {
      const updated = await api.updateUser(userId, { inactivityTimeout: value ?? undefined });
      if (onUserUpdate) onUserUpdate(updated);
      setUserMsg(prev => ({ ...prev, [userId]: { type: 'success', text: 'Mis à jour' } }));
    } catch {
      setUserMsg(prev => ({ ...prev, [userId]: { type: 'error', text: 'Erreur' } }));
    } finally {
      setSavingUserId(null);
      setTimeout(() => setUserMsg(prev => { const n = { ...prev }; delete n[userId]; return n; }), 3000);
    }
  };

  const fetchStats = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
      const params = new URLSearchParams({ action: 'get_connection_stats', date_from: dateFrom, date_to: dateTo });
      const res = await fetch(`${API_BASE}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setStats([]); }
      else { setStats(data.stats || []); }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, [dateFrom, dateTo]);

  const maxSeconds = stats.length > 0 ? Math.max(...stats.map(s => s.total_seconds)) : 0;

  return (
    <div className="p-10 animate-in fade-in space-y-10">

      {/* ── Section : Déconnexion automatique ── */}
      <div>
        <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Déconnexion automatique</h3>
        <p className="text-slate-500 text-sm mb-8">Configurez le délai d'inactivité avant déconnexion automatique. Le réglage par utilisateur est prioritaire sur le défaut de la compagnie.</p>

        {/* Délai par défaut de la compagnie */}
        <div className="p-6 rounded-2xl border border-slate-200 bg-slate-50 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-black text-slate-800 uppercase tracking-wide">Délai par défaut (compagnie)</div>
              <div className="text-xs text-slate-400 mt-0.5">Appliqué à tous les utilisateurs sans réglage spécifique</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-black text-indigo-600">{companyTimeout} min</span>
            </div>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESET_MINUTES.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setCompanyTimeout(m)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                  companyTimeout === m
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-400 hover:text-indigo-600'
                }`}
              >
                {m >= 60 ? `${m / 60}h` : `${m} min`}
              </button>
            ))}
            {/* Saisie libre */}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1}
                max={480}
                title="Délai personnalisé en minutes"
                placeholder="min"
                value={companyTimeout}
                onChange={e => setCompanyTimeout(Math.max(1, Math.min(480, parseInt(e.target.value) || 1)))}
                className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-bold text-center focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <span className="text-xs text-slate-400">min</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveCompanyTimeout}
              disabled={savingCompany}
              className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-all disabled:opacity-60 shadow"
            >
              {savingCompany ? 'Sauvegarde…' : 'Enregistrer'}
            </button>
            {companyMsg && (
              <span className={`text-xs font-bold ${companyMsg.type === 'success' ? 'text-emerald-600' : 'text-rose-500'}`}>
                {companyMsg.text}
              </span>
            )}
          </div>
        </div>

        {/* Réglages par utilisateur */}
        {users.length > 0 && (
          <div className="border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="text-sm font-black text-slate-800 uppercase tracking-wide">Réglage par utilisateur</div>
              <div className="text-xs text-slate-400 mt-0.5">Laisser vide pour utiliser le délai par défaut de la compagnie ({companyTimeout} min)</div>
            </div>
            <div className="divide-y divide-slate-100">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-4 px-6 py-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-black flex-shrink-0">
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">{u.name}</div>
                    <div className="text-xs text-slate-400 truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number"
                      min={1}
                      max={480}
                      value={userTimeouts[u.id] ?? ''}
                      onChange={e => setUserTimeouts(prev => ({ ...prev, [u.id]: e.target.value }))}
                      placeholder={`${companyTimeout} (défaut)`}
                      className="w-28 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-center focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <span className="text-xs text-slate-400">min</span>
                    <button
                      type="button"
                      onClick={() => handleSaveUserTimeout(u.id)}
                      disabled={savingUserId === u.id}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-all disabled:opacity-60"
                    >
                      {savingUserId === u.id ? '…' : 'OK'}
                    </button>
                    {userMsg[u.id] && (
                      <span className={`text-xs font-bold ${userMsg[u.id].type === 'success' ? 'text-emerald-600' : 'text-rose-500'}`}>
                        {userMsg[u.id].text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Section : Historique de connexion ── */}
      <div>
        <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Temps de connexion</h3>
        <p className="text-slate-500 text-sm mb-6">Durée de session par utilisateur sur une période donnée</p>

        {/* Filtres */}
        <div className="flex flex-wrap items-end gap-4 mb-8">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Du</label>
            <input
              type="date"
              title="Date de début"
              value={dateFrom}
              max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400 bg-slate-50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Au</label>
            <input
              type="date"
              title="Date de fin"
              value={dateTo}
              min={dateFrom}
              onChange={e => setDateTo(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400 bg-slate-50"
            />
          </div>
          <button
            type="button"
            onClick={fetchStats}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold transition-all disabled:opacity-60 shadow"
          >
            {loading ? 'Chargement…' : 'Analyser'}
          </button>

          <div className="flex gap-2 ml-auto flex-wrap">
            {[
              { label: "Aujourd'hui", from: today, to: today },
              { label: '7 jours', from: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10), to: today },
              { label: 'Ce mois', from: firstOfMonth, to: today },
            ].map(p => (
              <button
                type="button"
                key={p.label}
                onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-500 hover:border-violet-400 hover:text-violet-600 transition-all"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-600 text-sm">{error}</div>
        )}

        {searched && !loading && stats.length === 0 && !error && (
          <div className="text-center py-16 text-slate-400 text-sm">
            Aucune donnée de connexion pour cette période.
          </div>
        )}

        {stats.length > 0 && (
          <div className="space-y-3">
            {stats.map(u => (
              <div key={u.user_id} className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div
                  className="flex items-center gap-4 px-6 py-4 cursor-pointer select-none"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                >
                  <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-base flex-shrink-0">
                    {u.user_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="w-44 flex-shrink-0">
                    <div className="font-semibold text-slate-800 text-sm truncate">{u.user_name}</div>
                    <div className="text-xs text-slate-400">{u.session_count} session{u.session_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-indigo-400 rounded-full transition-all duration-500"
                        style={{ width: barWidth(u.total_seconds, maxSeconds) }}
                      />
                    </div>
                  </div>
                  <div className="w-24 text-right flex-shrink-0">
                    <span className="text-sm font-bold text-slate-700">{formatDuration(u.total_seconds)}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${expandedUser === u.user_id ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {expandedUser === u.user_id && u.sessions.length > 0 && (
                  <div className="border-t border-slate-100 bg-slate-50 px-6 py-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400 uppercase tracking-wide">
                          <th className="text-left py-1 pr-4 font-semibold">Connexion</th>
                          <th className="text-left py-1 pr-4 font-semibold">Déconnexion</th>
                          <th className="text-left py-1 pr-4 font-semibold">Durée</th>
                          <th className="text-left py-1 font-semibold">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.sessions.map((s, i) => (
                          <tr key={i} className="border-t border-slate-100 hover:bg-white transition-colors">
                            <td className="py-1.5 pr-4 text-slate-600">{formatDateTime(s.login)}</td>
                            <td className="py-1.5 pr-4 text-slate-600">{formatDateTime(s.logout)}</td>
                            <td className="py-1.5 pr-4 font-semibold text-slate-700">{formatDuration(s.duration)}</td>
                            <td className="py-1.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                s.type === 'logout' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {s.type === 'logout' ? 'Déconnexion' : 'Inactivité'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {expandedUser === u.user_id && u.sessions.length === 0 && (
                  <div className="border-t border-slate-100 bg-slate-50 px-6 py-4 text-xs text-slate-400">
                    Aucune session complète dans cette période (session peut-être encore active).
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionTimeTab;
